
import React from 'react';
import type { CanonicalEntityIds, PageArchetype, ResolvedPageContext } from './domain/resolvedPageContext';

// Added missing Page enum
export enum Page {
    Home = 'Home',
    Repairs = 'Repairs',
    Accessories = 'Accessories',
    ImeiVerification = 'ImeiVerification',
    Contact = 'Contact',
    FindMyModel = 'FindMyModel',
    Devices = 'Devices',
    BuyPhone = 'BuyPhone',
    SellPhone = 'SellPhone',
    ProductDetail = 'ProductDetail',
    AccessoryDetail = 'AccessoryDetail',
    Blog = 'Blog',
    Terms = 'Terms',
    Admin = 'Admin',
    LogoDesigner = 'LogoDesigner',
    Setup = 'Setup',
    Unsubscribe = 'Unsubscribe'
}

export enum AdminPage {
    Dashboard = 'Dashboard',
    POS = 'POS',
    RepairsManager = 'RepairsManager',
    Newsletter = 'Newsletter',
    SocialBroadcast = 'Social Broadcast',
    Appointments = 'Appointments',
    Labels = 'Labels',
    Inventory = 'Inventory',
    SalesOrders = 'Ventes',
    RepairOrders = 'Réparations',
    PurchaseOrders = 'Achats',
    BuybackRequests = 'Demandes Rachat',
    RevendreManager = 'Gestion Revendre',
    Quotes = 'Quotes',
    ProductRequests = 'Demandes Produits',
    Customers = 'Clients',
    Messages = 'Messages',
    Themes = 'Themes',
    Plugins = 'Plugins',
    SEO = 'SEO',
    SEOAnalytics = 'SEO & Analytics',
    SystemSettings = 'SystemSettings',
    BlogManager = 'BlogManager',
    LogoLab = 'Logo Lab',
    AICreativeStudio = 'AI Creative Studio',
    AIUsageHistory = 'AI Usage History',
    ReviewsManager = 'Avis & Vidéos',
    UserManagement = 'UserManagement',
    KeywordClusters = 'Keyword Clusters',
    GeoObservation = 'GeoObservation',
    GeoCanonical = 'GeoCanonical',
    GeoProvenance = 'GeoProvenance',
    SeoIntelDashboard = 'SEO Intel Dashboard',
    SeoIntelSearchConsole = 'SEO Intel Search Console',
    SeoIntelOpportunities = 'SEO Intel Opportunities',
    SeoIntelEntityGraph = 'SEO Intel Entity Graph',
    SeoIntelContentQuality = 'SEO Intel Content Quality',
    SeoIntelInternalLinks = 'SEO Intel Internal Links',
    SeoIntelTrends = 'SEO Intel Trends',
    SeoIntelCompetitors = 'SEO Intel Competitors',
    SeoIntelReviewQueue = 'SEO Intel Review Queue',
    SeoIntelContentGap = 'SEO Intel Content Gap',
    SeoIntelSettings = 'SEO Intel Settings',
    Observability = 'Observability'
}

export interface ServiceOfferOption {
    id: string;
    repairServiceId: number;
    displayLabel: string;
    qualityType?: string | null;
    customerDescription?: string | null;
    price: number | null;
    compareAtPrice?: number | null;
    durationOverride?: string | null;
    badge?: string | null;
    sortOrder: number;
    isDefault: boolean;
    isActive: boolean;
    availabilityState: 'AVAILABLE' | 'UNAVAILABLE' | 'QUOTE_ONLY';
    warrantyText?: string | null;
    provenanceClassification: 'UNVERIFIED' | 'COMPATIBLE' | 'AFTERMARKET' | 'OEM' | 'GENUINE' | 'OFFICIAL' | 'CERTIFIED' | 'REFURBISHED' | 'CUSTOMER_SUPPLIED' | 'OTHER';
    rowVersion?: number;
}

// Added missing RepairOption interface
export interface RepairPricePresentation {
    contract: 'PUBLIC_REPAIR_PRICE_PRESENTATION_V1';
    kind: 'EXACT' | 'STARTING_FROM' | 'FREE' | 'QUOTE';
    amount: number | null;
    amountLabel: string;
    isStartingFrom: boolean;
    label: string;
    currency: string | null;
}
export interface RepairOption {
    pricePresentation?: RepairPricePresentation;
    id: string;
    name: string;
    price: number | string;
    duration: string;
    description: string;
    focusKeyword?: string;
    isActive?: boolean;
    icon?: React.ReactNode;
    isStartingFrom?: boolean;
    originalPrice?: number | string;
    masterServiceId?: string;
    masterServiceSlug?: string;
    masterServiceName?: string;
    canonicalPath?: string;
    detailPageEligible?: boolean;
    detailCanonicalPath?: string | null;
    metaTitle?: string;
    metaDescription?: string;
    altText?: string;
    semanticArchetype?: string;
    semanticIntent?: string;
    semanticServiceFamily?: string;
    serviceOptions?: ServiceOfferOption[];
    selectedServiceOptionId?: string | null;
    selectedServiceOptionLabel?: string | null;
    serviceOptionSnapshot?: ServiceOfferOption | null;
}

// Added missing PhoneModel interface
export interface PhoneModel {
    id: string;
    masterStableId?: string;
    name: string;
    image: string;
    image_title?: string;
    image_alt?: string;
    image_keywords?: string;
    image_description?: string;
    image_services?: string;
    series: string;
    repairs: RepairOption[];
    repairsLoaded?: boolean;
    isActive?: boolean;
    condition?: string;
    salePrice?: number;
    stock?: number;
    description?: string;
    highlights?: string[];
    images360?: string[];
    brandName?: string;
    imei?: string;
    serialNumber?: string;
    videoUrl?: string;
      batteryHealth?: string;
    warranty?: string;
    storage?: string;
    camera?: string;
    specs?: {
        battery?: string;
        storage?: string;
        camera?: string;
        cpu?: string;
        ram?: string;
    };
    focusKeyword?: string;
    seoTitle?: string;
    seoDescription?: string;
    seoKeywords?: string;
    altText?: string;
    slug?: string;
    canonicalPath?: string;
    deviceKnowledgePath?: string;
    brandLogo?: React.ElementType;
    source?: 'inventory' | 'repair';
    color?: string;
    sim?: string;
    options?: ProductOptions;
}

export interface ProductOption {
    id: string;
    name: string;
    priceDiff: number;
    colorCode?: string;
}

export interface ProductOptions {
    colors?: ProductOption[];
    conditions?: ProductOption[];
    battery?: ProductOption[];
    warranty?: ProductOption[];
    sim?: ProductOption[];
    storage?: ProductOption[];
    quality?: ProductOption[];
}

// Added missing Brand interface
export interface Brand {
    id: string;
    name: string;
    slug?: string;
    canonicalPath?: string;
    logo: string;
    models: PhoneModel[];
    description?: string;
    focusKeyword?: string;
    seoTitle?: string;
    seoDescription?: string;
    isActive?: boolean;
}

export interface BackendService {
    id: number;
    brand: string;
    model: string;
    service_name: string;
    price: number;
    duration: string;
    brand_logo?: string;
    model_image?: string;
    images360?: string;
}

export interface BackendProduct {
    id: number;
    name: string;
    category: string;
    category_id?: number | null;
    categoryId?: number | null;
    modelId?: string | null;
    model_id?: string | null;
    deviceKnowledgePath?: string | null;
    repairModelPath?: string | null;
    mainCategory?: string;
    product_type?: string;
    productType?: string;
    catalogKind?: 'PHONE' | 'ACCESSORY' | 'OTHER';
    catalogProjectionAuthority?: string;
    image_url?: string;
    image?: string;
    gallery?: string | string[];
    highlights?: string | string[];
    price: number;
 currencyCode?: string;
    stock: number;
    brand: string;
    quality?: string;
    imei?: string;
    serialNumber?: string;
    sku?: string;
    description?: string;
    videoUrl?: string;
    video_url?: string;
    meta_title?: string;
    meta_description?: string;
    seoTitle?: string;
    seoDescription?: string;
    seo_title?: string;
    seo_description?: string;
    alt_text?: string;
    image_alt?: string;
    is_active?: number | boolean;
    battery_health?: string;
    batteryHealth?: string;
    condition?: string;
    color?: string;
    sim?: string;
    warranty?: string;
    storage?: string;
    camera?: string;
    options?: string | ProductOptions;
    slug?: string;
    canonicalPath?: string;
}

export interface GalleryImage {
    url: string;
    title?: string;
    alt?: string;
    description?: string;
}

// Added missing Part interface
export interface Part {
    id: number | string;
    name: string;
    mainCategory: string;
    productType: string;
    catalogKind: 'PHONE' | 'ACCESSORY' | 'OTHER';
    catalogProjectionAuthority?: string;
    categoryId?: number | string | null;
    category_id?: number | string | null;
    modelId?: string | null;
    model_id?: string | null;
    deviceKnowledgePath?: string | null;
    repairModelPath?: string | null;
    price: number;
 currencyCode?: string;
    image: string;
    gallery?: (string | GalleryImage)[];
    images360?: string[];
    sku: string;
    stock: number;
    brand: string;
    quality: string;
    description: string;
    highlights?: string[];
    focusKeyword?: string;
    altText?: string;
    slug?: string;
    canonicalPath?: string;
    seoTitle?: string;
    seoDescription?: string;
    videoUrl?: string;
    isArchived?: boolean;
    imei?: string;
    serialNumber?: string;
    warranty?: string;
    storage?: string;
    camera?: string;
    condition?: string;
    batteryHealth?: string;
    color?: string;
    sim?: string;
    options?: ProductOptions;
}

// Added missing BlogPost interface
export interface BlogPost {
    id: string;
    title: string;
    slug: string;
    canonicalPath?: string;
    content: string;
    excerpt: string;
    category: string;
    author: string;
    date: string;
    image: string;
    images360?: string[];
    tags: string[];
    seoTitle?: string;
    seoDescription?: string;
    focusKeyword?: string;
    altText?: string;
    isAiGenerated?: boolean;
    governedAssociations?: Array<{
        articleId: string;
        articlePublicId?: string;
        entityType: string;
        entityId: string;
        relationshipType: string;
        provenance: string;
    }>;
    status?: 'draft' | 'published';
    visualStoryPlan?: import('../shared/presentation/EditorialScrollStory').VisualStoryPlan | null;
}

// Added missing SeoData interface
export interface SeoData {
    title: string;
    description: string;
}

// Added missing BannerData interface
export interface BannerData {
    slogan: string;
    text: string;
    productName?: string;
}

// Added missing UnifiedCartItem interface
export interface UnifiedCartItem {
    id: string | number;
    name: string;
    price: number;
    quantity: number;
    image: string;
    category: 'Phone' | 'Part' | 'Repair';
    productType: string;
    color?: string;
    storage?: string;
    capacity?: string;
    condition?: string;
    battery?: string;
    warranty?: string;
    sim?: string;
    imei?: string;
    serialNumber?: string;
    selectedServiceOptionId?: string | null;
    selectedServiceOptionLabel?: string | null;
    serviceOptionSnapshot?: ServiceOfferOption | null;
}

// Added missing Order interface
export interface Order {
    id: number | string;
    orderId?: string;
    clientName: string;
    clientPhone?: string;
    clientEmail?: string;
    clientAddress?: string;
    items: UnifiedCartItem[];
    total: number;
    subtotal?: number;
    discount?: number;
    discountCode?: string;
    orderDate: Date | string;
    status?: 'Nouveau' | 'Confirmé' | 'En cours' | 'Terminé' | 'Annulé' | 'Remboursé';
    type?: string;
    paymentMethod?: string;
    paymentReference?: string;
    checkoutToken?: string;
    shippingOptionId?: string;
    notes?: string;
}

// Added missing Appointment interface
export interface Appointment {
    id: number | string;
    bookingRequestId?: string;
    documentReference?: string;
    publicAccessToken?: string;
    clientName: string;
    clientEmail: string;
    clientPhone: string;
    dateTime: string;
    model?: PhoneModel | null;
    repairs: RepairOption[];
    totalPrice: number;
    notes?: string;
    status: string;
    type?: 'appointment' | 'reminder';
    imei?: string;
    pdfBase64?: string;
    deliveryMode?: 'boutique' | 'postal';
    postalAddress?: string;
    postalZip?: string;
    postalCity?: string;
}

// Added missing QuoteRequest interface
export interface QuoteRequest {
    id?: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    city: string;
    zipCode: string;
    houseNumber: string;
    street: string;
    country: string;
    model: PhoneModel | Part | null;
    specs?: Record<string, string>;
    repairs: RepairOption[];
    totalPrice: number;
    pdfBase64?: string;
    remarks?: string;
    deviceColor?: { id: string; name: string; hex: string } | null;
    taxPresentation?: {
        mode: 'NOT_APPLICABLE' | 'INCLUSIVE' | 'EXCLUSIVE';
        ratePercent: number;
        included: boolean | null;
        currencyCode: string;
    } | null;
}

export interface BuybackRequest {
    documentReference?: string;
    id?: number;
    brand: string;
    model: string;
    capacity: string;
    condition_rating: string;
    battery_health: string;
    has_box: boolean;
    has_charger: boolean;
    has_invoice: boolean;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    customer_address?: string;
    customer_iban?: string;
    customer_twint?: string;
    estimated_price?: number;
    pdfBase64?: string;
    pdf_base64?: string;
    admin_notes?: string;
    status?: 'Pending' | 'Contacted' | 'Completed' | 'Rejected' | 'Accepted' | 'Reviewed';
    created_at?: string;
}

/** Canonical application form state for the public buyback journey. */
export interface SellPhoneFormData {
    brand: string;
    model: string;
    capacity: string;
    condition_rating: string;
    battery_health: string;
    has_box: boolean;
    has_charger: boolean;
    has_invoice: boolean;
    issue_face_id: boolean;
    issue_camera: boolean;
    issue_icloud: boolean;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    customer_address: string;
    customer_iban: string;
    customer_twint: string;
    agreedToTerms: boolean;
    subscribedToNewsletter: boolean;
}

export interface AdminUser {
    id: number;
    username: string;
    admin_path: string;
    created_at?: string;
}

export interface PageSeoInfo {
    id: string | number;
    url: string;
    title: string;
    description: string;
    keywords: string;
    canonical_url?: string;
    robots?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    twitter_card?: string;
    twitter_title?: string;
    twitter_description?: string;
    twitter_image?: string;
    indexing_status: 'Indexed' | 'Not Indexed' | 'not_indexed';
    last_indexed_at?: string;
    type: 'page' | 'product' | 'repair' | 'blog' | 'model';
    qualityScore?: number;
    lifecycle_state?: string;
    first_indexed_at?: string;
    last_crawled_at?: string;
    last_sitemap_submitted_at?: string;
    impressions?: number;
    clicks?: number;
    ctr?: number;
    avg_position?: number;
    coverage_status?: string;
    overall_score?: number;
    seo_score?: number;
    image_score?: number;
    schema_score?: number;
    internal_linking_score?: number;
    indexation_score?: number;
    content_score?: number;
    score_deductions?: unknown;
    seo_quality_override?: number;
}

export interface SeoGovernanceTask {
    id: number;
    task_type: string;
    affected_url: string;
    affected_entity_type: string;
    priority: 'High' | 'Medium' | 'Low';
    severity: 'Critical' | 'Warning' | 'Info';
    recommended_fix: string;
    status: 'Pending' | 'In Progress' | 'Completed' | 'Resolved';
    created_at?: string;
    updated_at?: string;
}

export interface AnalyticsStats {
    views: number;
    clicks: number;
    topPages: { url: string; views: number }[];
    userBehavior: { url: string; avgTime: number }[];
    historicalViewData: { date: string; views: number; clicks: number }[];
}

export interface BackendAppointment {
    id: number;
    customer_name: string;
    email: string;
    phone: string;
    appointment_date: string;
    device_model: string;
    selected_services: string | unknown[];
    notes: string;
    status: string;
    total_price?: number;
}

export interface BuybackModel {
    id: number;
    brand: string;
    model: string;
    slug?: string;
    capacity: string;
    base_price: number;
    condition_excellent_bonus: number;
    condition_good_bonus: number;
    condition_fair_bonus: number;
    is_active: boolean;
}

// Added missing FooterInfoData interface
export interface ShippingOption {
    id: string;
    name: string;
    price: number;
    description: string;
}

export interface FooterInfoData {
    nom_du_magasin: string;
    description_courte: string;
    telephone: string;
    email: string;
    adresse: string;
    lien_map: string;
    heures_ouverture: string;
    facebook_url: string;
    instagram_url: string;
    whatsapp_number: string;
    bouton_label: string;
    bouton_lien: string;
    twint_url?: string;
    twint_qr_base64?: string;
    shipping_options?: string | ShippingOption[];
    hero_bottom_text?: string;
    benefit1?: string;
    benefit2?: string;
    benefit3?: string;
    benefit4?: string;
    help_center_label?: string;
    seo_title: string;
    seo_description: string;
    seo_keywords: string;
    logo_url?: string;
    is_closed?: boolean;
    closed_reason?: string;
    closed_from?: string;
    closed_until?: string;
    closed_custom_message?: string;
    ga_tracking_id?: string;
    fb_pixel_id?: string;
    gtm_id?: string;
    google_site_verification?: string;
    tiktok_pixel_id?: string;
    snap_pixel_id?: string;
    pinterest_tag_id?: string;
    bing_webmaster_id?: string;
    whatsapp_token?: string;
    company_number?: string;
    booking_disabled_days?: number[];
    booking_hours_config?: string[];
    booking_disabled_dates?: string[];
    booking_special_hours?: Record<string, string[]>;
    whatsapp_phone_id?: string;
    whatsapp_business_id?: string;
    ai_chatbot_enabled?: boolean;
    promo_active?: boolean;
    promo_title?: string;
    promo_text?: string;
    promo_image?: string;
    promo_link?: string;
    sms_provider?: 'Twilio' | 'Infobip' | 'Custom';
    sms_api_key?: string;
    smtp_host?: string;
    smtp_port?: string;
    smtp_user?: string;
    smtp_pass?: string;
    smtp_encryption?: 'SSL/TLS' | 'STARTTLS' | 'NONE';
    smtp_protocol?: 'TLS' | 'SSL' | 'None';
    smtp_sender_name?: string;
    smtp_reply_to?: string;
    imap_host?: string;
    imap_port?: string;
    imap_user?: string;
    imap_pass?: string;
    imap_encryption?: 'SSL' | 'TLS' | 'None';
          bankName?: string;
    iban?: string;
    qrIban?: string;
    bic?: string;
        colors?: {
        gradientStart: string;
        gradientEnd: string;
        textOnDark: string;
        textOnLight: string;
    };
    // Added for modular tabs
    siteName?: string;
    metaTitle?: string;
    promoCode?: string;
    meta_title?: string;
    meta_description?: string;
    meta_keywords?: string;
    google_analytics_id?: string;
    facebook_pixel_id?: string;
  meta_pixel_id?: string;
    stripe_publishable_key?: string;
    stripe_secret_key?: string;
    notification_emails?: string;
    tax_rate?: string;
    tax_client_toggle_enabled?: boolean;
    tax_default_incl?: boolean;
    google_rating?: string;
    google_reviews_count?: number;
    google_review_url?: string | null;
    kanban_columns_config?: string;
    multi_repair_discount_enabled?: boolean;
    multi_repair_discount_trigger_count?: number;
    multi_repair_discount_type?: 'percent' | 'amount';
    multi_repair_discount_value?: number;
    color_selection_enabled?: boolean;
    color_selection_config?: string;
}

// Added missing CustomThemeConfig interface
export interface CustomThemeConfig {
    id: string;
    slug?: string;
    name: string;
    description?: string;
    category?: string;
    fontFamily: string;
    primaryColor: string;
    secondaryColor?: string;
    layoutType: 'standard' | 'injected' | 'prisma' | 'crystal';
    htmlStructure?: string;
    customCss?: string;
    isActive?: boolean;
    layout_json?: string;
    js_code?: string;
    tokens_json?: string;
    previewConfig?: string;
}

// Added missing HeroSlide interface
export interface HeroSlide {
    id: string;
    imageUrl: string;
    videoUrl?: string;
    title: string;
    subtitle: string;
    active?: boolean;
}

// Added missing PromotionSettings interface
export interface PromotionSettings {
    isEnabled: boolean;
    title: string;
    description: string;
    image: string;
    discountCode: string;
    discountPercent: number;
    buttonLabel: string;
    targetPage: Page;
    displayOnPages: Page[];
    startDate: string;
    endDate: string;
    showOncePerSession: boolean;
    displayDelay: number;
    overlayBlur: boolean;
    isDismissible: boolean;
}

// Added missing FindMyModelStep interface
export interface FindMyModelStep {
    id: number;
    title: string;
    description: string;
    steps: string[];
    image: string;
    alt: string;
}

// Added missing Subscriber interface
export interface Subscriber {
    id: string;
    email: string;
    dateAdded: string;
    status: 'active' | 'unsubscribed' | 'inactive';
    timestamp?: string | number | Date;
}

// Added missing PurchaseItem interface
export interface PurchaseItem {
    id: string;
    name: string;
    quantity: number;
    imeis: string[];
    priceOffered: number;
    expectedSellPrice: number;
    images: string[];
    battery_health?: string;
}

// Added missing PurchaseOrder interface
export interface PurchaseOrder {
    id: string;
    invoiceNumber: string;
    clientName: string;
    clientFirstName: string;
    clientPhone: string;
    clientEmail: string;
    clientAddress: string;
    items: PurchaseItem[];
    totalPurchasePrice: number;
    totalExpectedSellPrice: number;
    updatedAt: string;
    deviceName: string;
    imei: string;
}

export interface QuoteDevice {
    id: string;
    model: PhoneModel | { id: string, name: string, repairs: RepairOption[], isCustom: boolean } | null;
    isRepairable: boolean;
    selectedRepairs: RepairOption[];
    notRepairableReasons: string[];
    diagnosticFee: number;
    imei: string;
    customNote: string;
    images: string[];
}

export interface Quote {
    id: string;
    customer: {
        name: string;
        email: string;
        phone: string;
        address: string;
    };
    devices: QuoteDevice[];
    totalAmount: number;
    date: string;
    isWeb?: boolean;
    status?: string;
    dbId?: number;
    rawDate?: number;
    details?: Record<string, unknown>;
}

// Added missing AppContextType interface
export interface KanbanColumnConfig {
    label: string;
    color: string;
}

export interface DashboardSummary {
    total?: number;
    todayTotal?: number;
    appointments?: number;
    orders?: number;
    purchaseOrders?: number;
    quotes?: number;
    webQuotes?: number;
    messages?: number;
    
    // Real Counts & Sums
    allOrdersCount?: number;
    allRepairsCount?: number;
    allQuotesCount?: number;
    allWebQuotesCount?: number;
    allPurchaseOrdersCount?: number;
    allBuybackRequestsCount?: number;
    allProductsCount?: number;
    allCategoriesCount?: number;
    totalSalesSum?: number;
    totalPurchasesSum?: number;

    monthlyRevenue?: { month: string; amount: number }[];
    isMock?: boolean;
    system?: {
        cpu: number;
        ram: number;
        disk: number;
        latency: number;
    };
    activity?: {
        recent: {
            id: string;
            type: 'order' | 'appointment' | 'quote' | 'message';
            title: string;
            time: string;
            amount?: number;
            status?: string;
        }[];
    };
}

export interface LabelItem extends Part {
    qty: number;
    customImei?: string;
}

export interface NavItemProps {
    active: boolean;
    onClick: () => void;
    label: string;
    icon: React.ReactNode;
    activeBg: string;
    hoverBg: string;
    badge?: number;
}

export interface SidebarSectionProps {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
}

export interface WidgetProps {
    title: string;
    children: React.ReactNode;
    className?: string;
    delay?: number;
}
export interface AppNotification {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    submessage?: string;
    duration?: number;
}

export interface AppContextType {
    resolvedPageContext: ResolvedPageContext | null;
    navigateToEntity: (pageArchetype: PageArchetype, ids?: CanonicalEntityIds, mode?: 'push' | 'replace' | 'assign') => Promise<void>;
    repairData: Brand[];
    setRepairData: React.Dispatch<React.SetStateAction<Brand[]>>;
    // ... notifications
    notifications: AppNotification[];
    notify: (message: string, type?: AppNotification['type'], submessage?: string, duration?: number) => void;
    dismissNotification: (id: string) => void;
    
    selectedBrand: Brand | null;
    setSelectedBrand: React.Dispatch<React.SetStateAction<Brand | null>>;
    selectedModel: PhoneModel | null;
    setSelectedModel: React.Dispatch<React.SetStateAction<PhoneModel | null>>;
    selectedSeries: string | null;
    setSelectedSeries: React.Dispatch<React.SetStateAction<string | null>>;
    selectedRepairs: RepairOption[];
    setSelectedRepairs: React.Dispatch<React.SetStateAction<RepairOption[]>>;
    page: Page;
    setPage: React.Dispatch<React.SetStateAction<Page>>;
    cart: UnifiedCartItem[];
    addToCart: (item: PhoneModel | Part | UnifiedCartItem, quantity: number, category: UnifiedCartItem['category']) => void;
    updateCartQuantity: (id: string | number, category: string, qty: number) => void;
    removeFromCart: (id: string | number, category: string) => void;
    isCartOpen: boolean;
    setIsCartOpen: React.Dispatch<React.SetStateAction<boolean>>;
    orders: Order[];
    addOrder: (o: Order) => void;
    setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
    parts: Part[];
    setParts: React.Dispatch<React.SetStateAction<Part[]>>;
    heroSlides: HeroSlide[];
    setHeroSlides: React.Dispatch<React.SetStateAction<HeroSlide[]>>;
    promotionSettings: PromotionSettings;
    setPromotionSettings: React.Dispatch<React.SetStateAction<PromotionSettings>>;
    blogPosts: BlogPost[];
    setBlogPosts: React.Dispatch<React.SetStateAction<BlogPost[]>>;
    appointments: Appointment[];
    setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
    addAppointment: (a: Appointment) => Promise<Appointment>;
    quoteRequests: QuoteRequest[];
    addQuoteRequest: (q: QuoteRequest) => Promise<void>;
    buybackRequests: BuybackRequest[];
    addBuybackRequest: (b: BuybackRequest) => Promise<void>;
    footerInfo: FooterInfoData;
    setFooterInfo: React.Dispatch<React.SetStateAction<FooterInfoData>>;
    isAdmin: boolean;
    login: (u: string, p: string) => Promise<boolean>;
    logout: () => void;
    savedThemes: CustomThemeConfig[];
    activeThemeConfig: CustomThemeConfig | null;
    importNewTheme: (t: CustomThemeConfig) => Promise<boolean>;
    updateTheme: (id: string, updates: Partial<CustomThemeConfig>) => Promise<void>;
    deleteTheme: (id: string) => void;
    setActiveTheme: (id: string) => void;
    deactivateThemes?: () => Promise<void>;
    triggerIndexing: (n: string, p: string) => void;
    trackClick: (target: string, label?: string) => void;
    refreshConfig: () => Promise<void>;
    findMyModelSteps: FindMyModelStep[];
    selectedPhoneForPurchase: Part | null;
    setSelectedPhoneForPurchase: React.Dispatch<React.SetStateAction<Part | null>>;
    selectedPartForPurchase: Part | null;
    setSelectedPartForPurchase: React.Dispatch<React.SetStateAction<Part | null>>;
    selectedBlogPost: BlogPost | null;
    setSelectedBlogPost: React.Dispatch<React.SetStateAction<BlogPost | null>>;
    setCart: React.Dispatch<React.SetStateAction<UnifiedCartItem[]>>;
    sellPhoneStep: number;
    setSellPhoneStep: React.Dispatch<React.SetStateAction<number>>;
    sellPhoneFormData: SellPhoneFormData;
    setSellPhoneFormData: React.Dispatch<React.SetStateAction<SellPhoneFormData>>;
    sharedFormData: Record<string, unknown> | null;
    setSharedFormData: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
    isCheckoutOpen: boolean;
    setIsCheckoutOpen: React.Dispatch<React.SetStateAction<boolean>>;
    showcaseProducts?: string[];
    setShowcaseProducts?: React.Dispatch<React.SetStateAction<string[]>>;
    language: 'fr' | 'de' | 'en';
    setLanguage: (lang: 'fr' | 'de' | 'en') => void;
    t: (key: string, defaultText?: string) => string;
}
